/* eslint-disable camelcase */
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { when, reaction } from 'mobx';
import { observer, useLocalStore } from 'mobx-react';
import { Link } from 'react-mobx-router5';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
  FormGroup,
} from 'react-bootstrap';
import {
  CatalogLayout,
  CenteredLoadingSpinner,
  useFeature,
} from '@ole-ui/ole-ui-components';
import { Button, Breadcrumb, BreadcrumbItem } from '@patternfly/react-core';
import { Helmet } from 'react-helmet';
import UltimatePagination from 'react-ultimate-pagination-bootstrap-3';
import { withErrorBoundary } from 'react-error-boundary';
import { DateTime } from 'luxon';
import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import ViewLoader from 'components/Loading/View';
import Filters from 'components/PremiumFilter';
import TableColumn from 'components/Table/Column';
import InstructorName from 'components/SessionDetails/InstructorName';
import { storesContext } from 'stores';
import { formatDateTime } from 'services/UtilityService';
import SessionDetails from 'components/SessionDetails';
import { EnablementFeatureFlags } from 'config/featureFlags';
import {
  PREMIUM_SCHEDULED_SESSION_SORT_FILEDS,
  PREMIUM_VT_SESSION_SEATS_RANGE,
} from 'config/constants';
import ScheduleSessionStore from './store';
import './styles.scss';

const ScheduleSession = () => {
  const { i18n, t } = useTranslation();
  const {
    catalogStore,
    routerStore,
    userStore,
    vocabularyStore,
    premiumSessionsStore,
    premiumSessionFilterStore,
    classesStore,
  } = useContext(storesContext);

  const locale = i18n.language;

  const scheduleSessionStore = useLocalStore(
    () =>
      new ScheduleSessionStore(
        catalogStore,
        premiumSessionFilterStore,
        classesStore,
        routerStore,
      ),
  );

  const route = routerStore?.route as any;
  const pageTitle = route?.params?.title;

  const { currentPage, paginatedEntries, setCurrentPage, totalPages } =
    scheduleSessionStore;

  const showPremiumSessionsWithScheduleDisabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.ShowPremvtScheduleDisabled,
    defaultFlagValue: false,
  });

  // eslint-disable-next-line @typescript-eslint/default-param-last
  const presetFilters = (clear = false, routerName) => {
    if (clear && routerName === 'premium:sessions:live') {
      catalogStore.clearFilters();
      premiumSessionFilterStore.clearFilters();
      premiumSessionFilterStore.getFilterInfo(i18n, t, vocabularyStore);
    }

    scheduleSessionStore.initSelectedSessionToEnroll();
    catalogStore.presetFilters();
    premiumSessionFilterStore.presetFilters({
      instructors: premiumSessionFilterStore
        .flattenFilters('instructors')
        .join(','),
    });
  };

  const premSessionSlugSplitted =
    scheduleSessionStore.premSessionSlug?.split('-');

  const setIsRequestingSession = () => {
    const { sessionslug } = route.params;
    scheduleSessionStore.isRequestingSession =
      !premiumSessionsStore.sessionSlugHasScheduledSessions(sessionslug);
  };

  const courseSlug = premSessionSlugSplitted.length
    ? `${premSessionSlugSplitted[0]}-${premSessionSlugSplitted[1]}`
    : '';

  useEffect(() => {
    // Populate catalog data and filters on initial load - this happens on hard browser refresth
    when(
      () => !catalogStore.loaded && userStore.subscription.subscription,
      async () => {
        catalogStore.getFilterInfo();
      },
    );

    setIsRequestingSession();
  }, []);

  // Premium sessions
  useEffect(() => {
    // // Select preset filters on initial load
    when(
      () =>
        catalogStore.loaded &&
        premiumSessionsStore.scheduledSessionsloaded &&
        route?.name,

      async () => {
        scheduleSessionStore.premSessionSlug = route?.params.sessionslug;

        premiumSessionFilterStore.getFilterInfo(
          i18n,
          t,
          vocabularyStore,
          scheduleSessionStore.filterToApply,
        );
        const { getVocabularyByNamespace } = vocabularyStore;
        scheduleSessionStore.languages = await getVocabularyByNamespace(
          'instruction_languages',
          'en-US',
        );

        scheduleSessionStore.ready = true;
      },
    );

    // Update preset filters when route changes
    reaction(
      () =>
        catalogStore.loaded &&
        premiumSessionsStore.scheduledSessionsloaded &&
        routerStore?.route?.name,
      () => presetFilters(true, routerStore?.route?.name),
    );

    // Check for session availability to display request form
    reaction(
      () => premiumSessionsStore.scheduledSessionsloaded,
      () => {
        setIsRequestingSession();
      },
    );
  }, []);

  const SubscriptionExpiredModal = () => (
    <Modal
      show={scheduleSessionStore.showSubscriptionExpiredModal}
      onHide={scheduleSessionStore.toggleSubscriptionExpiredModal}
      size="sm"
      dialogClassName="subscription-expired-modal container"
    >
      <ModalHeader closeButton>
        <ModalTitle>
          <strong>{t('Warning')}</strong>
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <p>
          {t('Your subscription will expire on')}{' '}
          {DateTime.fromISO(userStore.subscriptionEndTime).toFormat(
            'yyyy/MM/dd',
          )}{' '}
          {t(
            'which is before the end date of this session. Please renew your subscription to select this session, or choose another date from the schedule.',
          )}
        </p>
        <p className="subscription-expired-modal-close-button">
          <Button
            variant="primary"
            onClick={scheduleSessionStore.toggleSubscriptionExpiredModal}
          >
            {t('Close')}
          </Button>
        </p>
      </ModalBody>
    </Modal>
  );

  const ScheduleSessionRow = (p) => {
    const { entry, instructorLanguage } = p;
    const isSessionFull =
      premiumSessionsStore.convertAvailableSeatsToRange(
        entry.seats_available,
      ) === PREMIUM_VT_SESSION_SEATS_RANGE.full;
    const isSessionAfterSubscriptionEnds =
      DateTime.fromISO(entry.end_time) >
      DateTime.fromISO(userStore.subscriptionEndTime);
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!isSessionFull && !isSessionAfterSubscriptionEnds)
            scheduleSessionStore.onSelectSessionToSchedule(entry.doc_id);
          else if (isSessionAfterSubscriptionEnds)
            scheduleSessionStore.toggleSubscriptionExpiredModal();
        }}
        className={`session-row ${isSessionFull ? 'full' : ''} ${
          entry.selected ? 'selected' : ''
        } ${isSessionAfterSubscriptionEnds ? ' disabled' : ''}`}
      >
        <div className="session-column-row">
          <div className="col-sm-2">
            <span className="sp-sm-header">{t('Date')} &nbsp; &nbsp; </span>
          </div>
          <div className="col-sm-10 col-md-12">
            {!isSessionFull && (
              <input
                type="radio"
                id={entry.doc_id}
                name="rd-session"
                className="rd-session-radio"
                defaultChecked={entry.selected}
                value={entry.doc_id}
                disabled={isSessionAfterSubscriptionEnds}
              />
            )}
            <label htmlFor={entry.doc_id}>
              {entry.start_time
                ? formatDateTime(entry.start_time, locale, DateTime.DATE_MED)
                : ''}
            </label>
          </div>
        </div>
        <div className="session-column-row">
          <div className="col-sm-2">
            <span className="sp-sm-header">{t('Time')} &nbsp; &nbsp; </span>
          </div>
          <div className="col-sm-10 col-md-12">
            {entry.start_time
              ? formatDateTime(entry.time, locale, DateTime.TIME_SIMPLE)
              : ''}
          </div>
        </div>
        <div className="session-column-row">
          <div className="col-sm-2">
            <span className="sp-sm-header">{t('Language')} &nbsp; &nbsp; </span>
          </div>
          <div className="col-sm-10 col-md-12">{instructorLanguage}</div>
        </div>
        <div className="session-column-row">
          <div className="col-sm-2">
            <span className="sp-sm-header">
              {t('Instructors')}&nbsp; &nbsp;
            </span>
          </div>
          <div className="col-sm-10 col-md-12">
            {entry?.instructors?.length
              ? entry.instructors
                  .map((instructor) => (
                    <InstructorName
                      instructor={premiumSessionsStore.getDetailsForInstructor(
                        instructor,
                      )}
                    />
                  ))
                  .reduce((prev, curr) => [prev, ', ', curr])
              : '---'}
          </div>
        </div>
        <div className="session-column-row">
          <div className="col-sm-2">
            <span className="sp-sm-header">{t('Seats')}&nbsp; &nbsp;</span>
          </div>
          <div className="col-sm-10 col-md-12">
            {entry?.seats_available >= 0
              ? premiumSessionsStore.convertAvailableSeatsToRange(
                  entry.seats_available,
                )
              : t('No info')}
          </div>
        </div>
      </div>
    );
  };
  const renderScheduleSessionColumn = () => (
    <React.Fragment>
      <TableColumn
        isSortAscending={scheduleSessionStore.isSortAscending}
        currentSortByField={scheduleSessionStore.sortByField}
        sortByField={PREMIUM_SCHEDULED_SESSION_SORT_FILEDS.date}
        onSort={(sortByField) => {
          scheduleSessionStore.onSortBy(sortByField);
        }}
        title={t('Date')}
      />
      <TableColumn
        isSortAscending={scheduleSessionStore.isSortAscending}
        title={t('Time')}
        currentSortByField={scheduleSessionStore.sortByField}
        sortByField={PREMIUM_SCHEDULED_SESSION_SORT_FILEDS.time}
        onSort={(sortByField) => {
          scheduleSessionStore.onSortBy(sortByField);
        }}
        includeInfoIcon
      />
      <TableColumn
        isSortAscending={scheduleSessionStore.isSortAscending}
        title={t('Language')}
        currentSortByField={scheduleSessionStore.sortByField}
        sortByField={PREMIUM_SCHEDULED_SESSION_SORT_FILEDS.language}
        onSort={(sortByField) => {
          scheduleSessionStore.onSortBy(sortByField);
        }}
      />

      <TableColumn
        isSortAscending={scheduleSessionStore.isSortAscending}
        currentSortByField={scheduleSessionStore.sortByField}
        sortByField={PREMIUM_SCHEDULED_SESSION_SORT_FILEDS.instructor}
        onSort={(sortByField) => {
          scheduleSessionStore.onSortBy(sortByField);
        }}
        title={t('Instructor')}
      />

      <TableColumn
        isSortAscending={scheduleSessionStore.isSortAscending}
        currentSortByField={scheduleSessionStore.sortByField}
        sortByField={PREMIUM_SCHEDULED_SESSION_SORT_FILEDS.availableSeats}
        onSort={(sortByField) => {
          scheduleSessionStore.onSortBy(sortByField);
        }}
        title={t('Seats')}
      />
    </React.Fragment>
  );

  const renderSubmitProgressModal = () => (
    <React.Fragment>
      <Modal
        size="sm"
        show={scheduleSessionStore.isShowASubmitProgress}
        onHide={() => {
          scheduleSessionStore.toggleSubmitProgressModal();
        }}
        dialogClassName="schedule-progress-modal-dialog container"
      >
        <ModalHeader closeButton>
          <ModalTitle
            className={
              !scheduleSessionStore.submitProgress?.hasError
                ? 'modal-title success-message'
                : 'modal-title error-message'
            }
          >
            {`${t('Schedule Session')}: `}
            {!scheduleSessionStore.submitProgress?.hasError
              ? t('Success')
              : t('Error')}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="model-wrapper">
            <FormGroup>
              <div className="progress-message">
                {scheduleSessionStore.submitProgress.message}
              </div>
              <div className="progress-message-button">
                <Button
                  variant="primary"
                  onClick={() => {
                    scheduleSessionStore.toggleSubmitProgressModal();
                    if (!scheduleSessionStore.submitProgress.hasError) {
                      routerStore.navigate('premium:sessions:live');
                    }
                  }}
                >
                  {t('OK')}
                </Button>
              </div>
            </FormGroup>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );

  const renderContent = () => {
    if (!scheduleSessionStore.ready) {
      return (
        <div className="schedule-container__loading-container">
          <CenteredLoadingSpinner />
        </div>
      );
    }

    return (
      <div className="schedule-container">
        <Breadcrumb>
          <BreadcrumbItem
            component={Link}
            to="premium:sessions:live"
            onClick={() => routerStore.navigate('premium:sessions:live')}
          >
            {t('Live Sessions Catalog')}
          </BreadcrumbItem>
          <BreadcrumbItem isActive>
            {t('Live sessions for {{offering}}', {
              offering: courseSlug,
            })}
          </BreadcrumbItem>
        </Breadcrumb>
        <SessionDetails
          data={{
            ...(premiumSessionsStore.groupedPremiumSessionContentByOffering[
              courseSlug
            ] || {}),
            duration_secs:
              premiumSessionsStore.getSessionSecsDurationBySessionSlug(
                scheduleSessionStore.premSessionSlug,
              ),
            premvt_session_slug: scheduleSessionStore.premSessionSlug,
          }}
          showMetaData={scheduleSessionStore.isRequestingSession}
          isRequestingSession={scheduleSessionStore.isRequestingSession}
        />

        {paginatedEntries.length > 0 && (
          <section className="schedule-list-container">
            <div className="schedule-list-header">
              {renderScheduleSessionColumn()}
            </div>
            <React.Fragment>
              <div className="schedule-list-wrapper">
                {paginatedEntries.map((entry) => {
                  const { doc_id: docId } = entry;
                  const lan = scheduleSessionStore.languages?.find(
                    (l) => l.token === entry.instruction_language,
                  );

                  return (
                    <ScheduleSessionRow
                      entry={entry}
                      key={docId}
                      instructorLanguage={
                        // eslint-disable-next-line camelcase
                        (lan && lan?.display_name) || entry.instruction_language
                      }
                    />
                  );
                })}
              </div>
              <div className="schedule-pagination">
                <UltimatePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={setCurrentPage}
                />
              </div>
              <div className="schedule-buttons">
                {scheduleSessionStore.isRescheduling && (
                  <Button
                    className="cancel-reschedule-button schedule-reschedule-btn"
                    onClick={() => {
                      routerStore.navigate('home');
                    }}
                  >
                    {t('Cancel')}
                  </Button>
                )}
                {showPremiumSessionsWithScheduleDisabled ||
                userStore.hasReachedCurrentSubscriptionPremiumVTLimit ? (
                  <Button
                    variant="link"
                    isDisabled
                    data-analytics-id="schedule-btn-session-scheduler-view-freeTier-lp"
                    className="disabled-patternfly-btn schedule-reschedule-btn"
                  >
                    {t('Schedule')}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    disabled={!scheduleSessionStore.isAllowToSchedule}
                    type="submit"
                    onClick={() => {
                      scheduleSessionStore.enrollUser(t);
                    }}
                    data-analytics-id="schedule-btn-session-scheduler-view-lp"
                    className="schedule-reschedule-btn"
                  >
                    {!scheduleSessionStore.submitting ? (
                      `${
                        scheduleSessionStore.isRescheduling
                          ? t('Reschedule')
                          : t('Schedule')
                      }`
                    ) : (
                      <span className="spinner-wrapper">
                        <ViewLoader color="#ffffff" />
                      </span>
                    )}
                  </Button>
                )}
              </div>
            </React.Fragment>
          </section>
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet title={`${pageTitle} - Red Hat Learning`} />
      <CatalogLayout>
        <CatalogLayout.Filters>
          {/* TODO: filters loading state with skeletons */}
          <Filters showFullSeatsFilter title={t('Schedule live session')} />
        </CatalogLayout.Filters>
        <CatalogLayout.Content>{renderContent()}</CatalogLayout.Content>
        {scheduleSessionStore.isShowASubmitProgress &&
          renderSubmitProgressModal()}
        {scheduleSessionStore.showSubscriptionExpiredModal &&
          SubscriptionExpiredModal()}
      </CatalogLayout>
    </>
  );
};

export default withErrorBoundary(
  observer(ScheduleSession),
  GenericViewError,
  handleError,
);
